import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Button,
    Typography,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    RadioGroup,
    Radio,
    FormControlLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    Chip,
    Card,
    CardContent,
    IconButton
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import PhoneIcon from '@mui/icons-material/Phone';
import { local_url, local_signage } from '../../Const';

// This screen is scoped to Retail customers only, so the Customer Type is
// fixed rather than user-selectable (matches the "RT" code from GetCustomerTypes).
const FIXED_CUSTOMER_TYPE = 'RT';

// Every existing usage of PricingSchemeType in the app is the literal value "HORECA".
const PRICING_SCHEME_TYPES = ['HORECA'];

const ADVANCED_SEARCH_LABELS = { CUSTCODE: 'Customer Code', MOBILE: 'Mobile Number', CUSTNAME: 'Customer Name' };

// Digits only, matches the CreditLimit/CreditDays numeric-only fields on the API.
const isDigitsOnly = (value) => /^\d*$/.test(value);

// Field casing isn't confirmed for this endpoint's response, so check the common variants.
const getBranchId = (branch) => branch?.BranchId || branch?.branchId || branch?.BranchID || branch?.branchID || '';
const getBranchName = (branch) => branch?.BranchName || branch?.branchName || getBranchId(branch);

// Shared Add/Edit popup for Local Credit Customer. In "edit" mode the customer is
// already known (from the list row) so the search UI is replaced by a read-only field.
export const LocalCreditCustomerForm = ({ open, mode, editingRecord, branches, loadingBranches, onClose, onSaved, showNotification, handleAuthError }) => {
    const token = localStorage.getItem('AuthorizationToken');
    const userId = localStorage.getItem('userId');
    const createdUserId = userId || 'ADMIN';

    const [selectedCustomer, setSelectedCustomer] = useState(null);

    const [advancedSearchType, setAdvancedSearchType] = useState('CUSTCODE');
    const [advancedSearchInput, setAdvancedSearchInput] = useState('');
    const [advancedSearchResults, setAdvancedSearchResults] = useState([]);
    const [loadingAdvancedSearch, setLoadingAdvancedSearch] = useState(false);

    const [forBranchId, setForBranchId] = useState('');
    const [creditLimit, setCreditLimit] = useState('');
    const [creditDays, setCreditDays] = useState('');
    const [pricingSchemeType, setPricingSchemeType] = useState(PRICING_SCHEME_TYPES[0]);
    const [creditLimitError, setCreditLimitError] = useState('');
    const [creditDaysError, setCreditDaysError] = useState('');

    const [isSubmitting, setIsSubmitting] = useState(false);

    // Reset/prefill the form whenever the dialog is (re)opened
    useEffect(() => {
        if (!open) return;
        if (mode === 'edit' && editingRecord) {
            setSelectedCustomer(null);
            setForBranchId(editingRecord.ForBranchId || editingRecord.forBranchId || '');
            setCreditLimit(String(editingRecord.CreditLimit ?? editingRecord.creditLimit ?? ''));
            setCreditDays(String(editingRecord.CreditDays ?? editingRecord.creditDays ?? ''));
            setPricingSchemeType(editingRecord.PricingSchemeType || editingRecord.pricingSchemeType || PRICING_SCHEME_TYPES[0]);
        } else {
            setSelectedCustomer(null);
            setForBranchId('');
            setCreditLimit('');
            setCreditDays('');
            setPricingSchemeType(PRICING_SCHEME_TYPES[0]);
        }
        setAdvancedSearchInput('');
        setAdvancedSearchResults([]);
        setCreditLimitError('');
        setCreditDaysError('');
    }, [open, mode, editingRecord]);

    const handleAdvancedSearch = async () => {
        if (!advancedSearchInput.trim()) {
            showNotification('Please enter a search term.', 'error');
            return;
        }
        if (!token) {
            handleAuthError();
            return;
        }
        setLoadingAdvancedSearch(true);
        try {
            const response = await fetch(
                `${local_url}/api/POSCustomerSerch/Get/${FIXED_CUSTOMER_TYPE}/${advancedSearchType}/${encodeURIComponent(advancedSearchInput.trim())}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error(`Search failed with status ${response.status}`);
            const data = await response.json();
            setAdvancedSearchResults(Array.isArray(data?.customers) ? data.customers : []);
        } catch (error) {
            console.error('Error running advanced customer search:', error);
            showNotification('Customer search failed.', 'error');
            setAdvancedSearchResults([]);
        } finally {
            setLoadingAdvancedSearch(false);
        }
    };

    const handleSelectAdvancedResult = (customer) => {
        setSelectedCustomer(customer);
        setAdvancedSearchResults([]);
    };

    const getCustomerLabel = (customer) => {
        if (!customer) return '';
        const code = customer.customerCode || customer.custID || '';
        const name = customer.customerName || '';
        return code && name ? `${code}-${name}` : name || code;
    };

    const getCustomerId = (customer) => customer?.customerCode || customer?.custID || '';

    const editingCustId = editingRecord?.CustId || editingRecord?.custId || '';

    // The dropdown only knows about "HORECA", so if an existing record carries a
    // different value, add it in so the current value still shows selected.
    const pricingSchemeTypeOptions = PRICING_SCHEME_TYPES.includes(pricingSchemeType) || !pricingSchemeType
        ? PRICING_SCHEME_TYPES
        : [pricingSchemeType, ...PRICING_SCHEME_TYPES];

    const isReadyToSave =
        mode === 'edit'
            ? !!forBranchId && creditLimit.trim() !== '' && creditDays.trim() !== '' && !!pricingSchemeType
            : !!selectedCustomer && !!forBranchId && creditLimit.trim() !== '' && creditDays.trim() !== '' && !!pricingSchemeType;

    const handleSubmit = async () => {
        if (mode !== 'edit' && !selectedCustomer) {
            showNotification('Please search for and select a customer first.', 'error');
            return;
        }
        if (!forBranchId || creditLimit.trim() === '' || creditDays.trim() === '' || !pricingSchemeType) {
            showNotification('Please complete Branch, Credit Limit, Credit Days, and Pricing Scheme Type.', 'error');
            return;
        }
        if (!token) {
            handleAuthError();
            return;
        }

        setIsSubmitting(true);

        const payload = {
            CustId: mode === 'edit' ? editingCustId : getCustomerId(selectedCustomer),
            ForBranchId: forBranchId,
            CreditLimit: Number(creditLimit),
            CreditDays: Number(creditDays),
            PricingSchemeType: pricingSchemeType,
            CreatedUserCode: createdUserId
        };

        try {
            // NOTE: there is currently only one backend endpoint (insertlocalcreditcustomer).
            // It's reused here for Edit too since the stored proc name suggests it already
            // upserts by CustId; switch this to a dedicated update endpoint once the backend
            // team confirms/adds one.
            const response = await fetch(`${local_signage}/api/v1/insertlocalcreditcustomer`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                let serverMessage = '';
                try {
                    const errorBody = await response.json();
                    serverMessage = errorBody?.message || '';
                } catch {
                    // response body wasn't JSON; fall back to the generic message below
                }
                throw new Error(serverMessage || `Request failed with status ${response.status}`);
            }

            const result = await response.json();

            if (result.success === false) {
                showNotification(result.message || 'The server rejected this request.', 'error');
                return;
            }

            showNotification(
                mode === 'edit'
                    ? 'Customer limit updated and submitted for approval.'
                    : 'Customer created and submitted for approval.',
                'success'
            );
            onSaved();
        } catch (error) {
            console.error('Error saving local credit customer:', error);
            showNotification(error.message || 'Failed to save. Please try again.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', color: '#004d00', fontWeight: 700 }}>
                <PersonIcon sx={{ mr: 1 }} /> {mode === 'edit' ? 'Edit Local Credit Customer' : 'New Local Credit Customer'}
                <IconButton onClick={onClose} sx={{ ml: 'auto', color: 'text.secondary' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                {mode === 'edit' ? (
                    <TextField
                        fullWidth
                        size="small"
                        label="Customer Code"
                        value={editingCustId}
                        InputProps={{ readOnly: true }}
                        sx={{ mt: 1, mb: 2 }}
                    />
                ) : (
                    <>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, mt: 1 }}>
                            Search Customer
                        </Typography>
                        <Grid container spacing={1.5} sx={{ mb: 2 }}>
                            <Grid item xs={12}>
                                <RadioGroup
                                    row
                                    value={advancedSearchType}
                                    onChange={(e) => setAdvancedSearchType(e.target.value)}
                                >
                                    <FormControlLabel value="CUSTCODE" control={<Radio size="small" />} label="Code" />
                                    <FormControlLabel value="MOBILE" control={<Radio size="small" />} label="Mobile" />
                                    <FormControlLabel value="CUSTNAME" control={<Radio size="small" />} label="Name" />
                                </RadioGroup>
                                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label={ADVANCED_SEARCH_LABELS[advancedSearchType]}
                                        value={advancedSearchInput}
                                        onChange={(e) => setAdvancedSearchInput(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleAdvancedSearch();
                                        }}
                                    />
                                    <Button
                                        variant="contained"
                                        onClick={handleAdvancedSearch}
                                        disabled={loadingAdvancedSearch}
                                        sx={{ minWidth: 44, px: 0, bgcolor: '#004d00', '&:hover': { bgcolor: '#003300' } }}
                                    >
                                        {loadingAdvancedSearch ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>

                        <Dialog
                            open={advancedSearchResults.length > 0}
                            onClose={() => setAdvancedSearchResults([])}
                            maxWidth="md"
                            fullWidth
                            PaperProps={{ sx: { borderRadius: '12px' } }}
                        >
                            <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
                                <PersonSearchIcon sx={{ mr: 1, color: '#004d00' }} /> Select Customer
                                <IconButton onClick={() => setAdvancedSearchResults([])} sx={{ ml: 'auto', color: 'text.secondary' }}>
                                    <CloseIcon />
                                </IconButton>
                            </DialogTitle>
                            <DialogContent>
                                <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                                    {advancedSearchResults.map((customer) => (
                                        <Grid item xs={12} sm={6} md={4} key={getCustomerId(customer)}>
                                            <Card
                                                onClick={() => handleSelectAdvancedResult(customer)}
                                                sx={{
                                                    border: '1px solid #e0e0e0',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': { transform: 'translateY(-2px)', boxShadow: 3, borderColor: '#004d00' }
                                                }}
                                            >
                                                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                                    <Typography variant="subtitle2" noWrap sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <PersonIcon sx={{ mr: 0.5, fontSize: '16px' }} />
                                                        {customer.customerName || '-'}
                                                    </Typography>
                                                    <Chip
                                                        icon={<PhoneIcon sx={{ fontSize: '14px' }} />}
                                                        label={customer.mobile || 'N/A'}
                                                        size="small"
                                                        sx={{ mt: 1, fontSize: '0.75rem', backgroundColor: '#e0e0e0' }}
                                                    />
                                                    <Chip
                                                        label={`ID: ${getCustomerId(customer) || 'N/A'}`}
                                                        size="small"
                                                        sx={{ mt: 1, ml: 1, fontSize: '0.75rem', backgroundColor: '#004d00', color: 'white' }}
                                                    />
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </DialogContent>
                        </Dialog>

                        <TextField
                            fullWidth
                            size="small"
                            label="Customer Name"
                            placeholder="Search Code / Mobile / Name above to select a customer"
                            value={selectedCustomer ? getCustomerLabel(selectedCustomer) : ''}
                            InputProps={{ readOnly: true }}
                            sx={{ mb: 2 }}
                        />
                    </>
                )}

                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                    Credit Details
                </Typography>

                <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Branch</InputLabel>
                            <Select
                                label="Branch"
                                value={forBranchId}
                                onChange={(e) => setForBranchId(e.target.value)}
                                disabled={loadingBranches}
                            >
                                {branches.map((branch) => (
                                    <MenuItem key={getBranchId(branch)} value={getBranchId(branch)}>
                                        {getBranchName(branch)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Pricing Scheme Type</InputLabel>
                            <Select
                                label="Pricing Scheme Type"
                                value={pricingSchemeType}
                                onChange={(e) => setPricingSchemeType(e.target.value)}
                            >
                                {pricingSchemeTypeOptions.map((type) => (
                                    <MenuItem key={type} value={type}>
                                        {type}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Credit Limit"
                            value={creditLimit}
                            error={!!creditLimitError}
                            helperText={creditLimitError}
                            onChange={(e) => {
                                if (isDigitsOnly(e.target.value)) {
                                    setCreditLimit(e.target.value);
                                    setCreditLimitError('');
                                } else {
                                    setCreditLimitError('Only numbers are allowed');
                                }
                            }}
                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Credit Days"
                            value={creditDays}
                            error={!!creditDaysError}
                            helperText={creditDaysError}
                            onChange={(e) => {
                                if (isDigitsOnly(e.target.value)) {
                                    setCreditDays(e.target.value);
                                    setCreditDaysError('');
                                } else {
                                    setCreditDaysError('Only numbers are allowed');
                                }
                            }}
                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                        />
                    </Grid>
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3, mb: 1 }}>
                    <Button onClick={onClose} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                        onClick={handleSubmit}
                        disabled={isSubmitting || !isReadyToSave}
                        sx={{ bgcolor: '#004d00', '&:hover': { bgcolor: '#003300' }, minWidth: 140 }}
                    >
                        {isSubmitting ? 'Saving...' : 'Save'}
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default LocalCreditCustomerForm;
